// Geidea Hosted Payment Page loader for Egypt (EGP) orders.
//
// Flow:
//   1) Backend creates a Geidea session and returns { session_id }.
//   2) We dynamically load https://www.merchant.geidea.net/hpp/geideaCheckout.min.js
//      (idempotent — re-uses an existing script tag if present).
//   3) We instantiate `new GeideaCheckout(onSuccess, onError, onCancel)` and
//      call `payment.startPayment(sessionId)` to show the popup overlay.
//
// Reference: https://docs.geidea.net/docs/geidea-checkout-v2

const SDK_URL = "https://www.merchant.geidea.net/hpp/geideaCheckout.min.js";

let _loadPromise = null;

export function loadGeideaSdk() {
  if (typeof window === "undefined") return Promise.reject(new Error("no-window"));
  if (window.GeideaCheckout) return Promise.resolve(window.GeideaCheckout);
  if (_loadPromise) return _loadPromise;

  _loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SDK_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.GeideaCheckout));
      existing.addEventListener("error", () => reject(new Error("geidea-sdk-load-failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = SDK_URL;
    s.async = true;
    s.onload = () => {
      if (window.GeideaCheckout) resolve(window.GeideaCheckout);
      else reject(new Error("geidea-sdk-missing-global"));
    };
    s.onerror = () => reject(new Error("geidea-sdk-load-failed"));
    document.head.appendChild(s);
  });

  return _loadPromise;
}

export async function startGeideaPayment({ sessionId, containerId, onSuccess, onError, onCancel }) {
  const Ctor = await loadGeideaSdk();
  const payment = new Ctor(onSuccess, onError, onCancel);
  // Geidea: startPayment(sessionId, integrationKey=null, containerId) — when a
  // containerId is provided the SDK renders into the matching <div> as an
  // embedded iframe (drop-in mode) instead of as a fullscreen popup overlay.
  if (containerId) {
    payment.startPayment(sessionId, null, containerId);
  } else {
    payment.startPayment(sessionId);
  }
  return payment;
}
